import React, { useState } from 'react';
import {
  Card,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { useSelector } from 'react-redux';

import { ReceiptFooter } from '../Configuration';
import { getProductGroups } from 'reducers/productGroupsDB';
import { Delete, Edit, ExpandLess, ExpandMore } from '@material-ui/icons';
import classNames from 'classnames';

const useStyles = makeStyles<Theme>(theme => ({
  truncatedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    lineHeight: '1.5em',
    display: 'block', // Fallback for non-webkit
    height: '3em', // Fallback for non-webkit, line-height * 2
    '@supports (display: -webkit-box) and (-webkit-line-clamp: 2) and (-webkit-box-orient: vertical)': {
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    },
  },
  footerTextContainer: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'space-between',
    gap: '0.5rem',
    width: '100%',
  },
  footerText: {
    whiteSpace: 'pre-wrap',
  },
  card: {
    display: 'flex',
    justifyContent: 'space-between',
    '& .MuiCardActions-root': {
      flexDirection: 'column',
      borderLeft: '1px solid',
      borderLeftColor: theme.palette.divider,
    },
    '& .MuiCardContent-root': {
      flexGrow: 1,
    },
  },
  chipContainer: {
    marginTop: '0.5rem',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem',
  },
}));

const DEFAULT_SHOWN_GROUP_AMOUNT = 5;

interface Props {
  footer: ReceiptFooter;
  onEditFooter(id: string): void;
  onDeleteFooter(id: string): void;
}

function ReceiptFooterCard({ footer, onEditFooter, onDeleteFooter }: Props) {
  const styles = useStyles();

  const productGroups = useSelector(getProductGroups);

  const [isExpanded, setIsExpanded] = useState(false);

  const hiddenGroupAmount =
    footer.productGroupIds.length - DEFAULT_SHOWN_GROUP_AMOUNT;

  return (
    <Card variant="outlined" className={styles.card}>
      <CardContent className={styles.cardContent}>
        <div className={styles.footerTextContainer}>
          <Typography
            className={classNames(styles.footerText, {
              [styles.truncatedText]: !isExpanded,
            })}
          >
            {footer.footerText}
          </Typography>
          <IconButton onClick={() => setIsExpanded(prev => !prev)}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>

        <div className={styles.chipContainer}>
          {footer.productGroupIds
            .slice(
              0,
              isExpanded
                ? footer.productGroupIds.length
                : DEFAULT_SHOWN_GROUP_AMOUNT,
            )
            .map(id => {
              const productGroup = productGroups.find(
                group => String(group.id) === id,
              );
              return <Chip label={productGroup?.name ?? 'Unknown group'} />;
            })}
          {!isExpanded && hiddenGroupAmount > 0 ? (
            <Typography color="textSecondary">
              and {hiddenGroupAmount} more group(s)
            </Typography>
          ) : null}
        </div>
      </CardContent>
      <CardActions disableSpacing>
        <IconButton onClick={() => onEditFooter(footer.id)}>
          <Edit />
        </IconButton>
        <IconButton onClick={() => onDeleteFooter(footer.id)}>
          <Delete />
        </IconButton>
      </CardActions>
    </Card>
  );
}

export default ReceiptFooterCard;
